import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useMediaQuery } from "react-responsive"
import { useRecoilState } from "recoil"
import * as yup from "yup"

import { Box, Button, Container, Stack, Typography } from "src/UILibrary"
import InputTextField from "src/components/atom/InputTextField"
import { OutlinedButton } from "src/components/buttons/OutlinedButton"
import { Title } from "src/components/layout/Title"
import { ShowToast } from "src/states/showToast"
import { RESPONSE_CODE, SNACKBAR_TYPES } from "src/modules/const"
import { useLoginContext } from "src/hooks/useLoginContext"
import { ContactusService } from "src/openapi"
import { validateFields } from "src/modules/validation"
import { ShowProgress } from "src/states/showProgress"

export const ContactUs = () => {
  const { t } = useTranslation("translation", { keyPrefix: "contact" })
  const [step, setStep] = useState<number>(0)

  const [, setShowProgress] = useRecoilState(ShowProgress)

  const [name, setName] = useState<string>("")
  const [email, setEmail] = useState<string>("")
  const [company, setCompany] = useState<string>("")
  const [context, setContext] = useState<string>("")

  const [nameValidationMessage, setNameValidationMessage] = useState<string>("")
  const [emailValidationMessage, setEmailValidationMessage] = useState<string>("")
  const [contextValidationMessage, setContextValidationMessage] = useState<string>("")

  const [, setShowToast] = useRecoilState(ShowToast)
  const isTablet = useMediaQuery({ query: "(min-width: 900px)" })
  const { initialized } = useLoginContext()

  useEffect(() => {
    if (!initialized) return
  }, [initialized])

  const validate = async () => {
    let validation = true

    if (!name) {
      validation = validation && false
      setNameValidationMessage(t("please_input_name"))
    } else {
      setNameValidationMessage("")
    }

    try {
      await validateFields(
        yup.object().shape({
          email: yup
            .string()
            .required(t("please_input_email"))
            .email(t("please_input_correct_email")),
        }),
        ["email"],
        [email]
      )
      setEmailValidationMessage("")
    } catch (err: any) {
      setEmailValidationMessage(err.message)
      validation = validation && false
    }

    if (!context) {
      validation = validation && false
      setContextValidationMessage(t("please_input_context"))
    } else {
      setContextValidationMessage("")
    }

    return validation
  }

  const onBack = () => {
    setStep(0)
  }

  const onNext = async () => {
    if (step === 0) {
      if (!(await validate())) {
        return
      }
      setStep(1)
    } else {
      setShowProgress(true)
      ContactusService.createContactus({
        name: name,
        email: email,
        company: company,
        context: context,
      })
        .then((res) => {
          setShowProgress(false)
          if (res.status === RESPONSE_CODE.CREATE_SUCCESS) {
            setShowToast({ show: true, text: t("sent"), type: SNACKBAR_TYPES.SUCCESS })
            setName("")
            setEmail("")
            setCompany("")
            setContext("")
          } else {
            setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
          }
        })
        .catch(() => {
          setShowProgress(false)
          setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
        })

      setStep(0)
    }
  }

  return (
    <>
      <Title title="CONTACT" sub_title={t("contact")} />

      <Container
        maxWidth="md"
        sx={{ px: isTablet ? "0px" : "10px", py: "10px", mx: "auto" }}
        className="main-body-with-footer"
      >
        <Box component="form" noValidate autoComplete="off" sx={{ pb: "30px" }}>
          <Stack spacing={5}>
            <Typography.SubTitleNormal color="textPrimary">
              {t("get_back_in_3_days")}
            </Typography.SubTitleNormal>

            <InputTextField
              required
              name={t("name")}
              readOnly={step === 1}
              text={name}
              handleText={setName}
              validationMessage={nameValidationMessage}
            />
            <InputTextField
              required
              name={t("email")}
              readOnly={step === 1}
              text={email}
              handleText={setEmail}
              validationMessage={emailValidationMessage}
            />
            <InputTextField
              name={t("company")}
              readOnly={step === 1}
              text={company}
              handleText={setCompany}
            />
            <InputTextField
              required
              name={t("content")}
              multiline
              placeholder={t("content_placeholder")}
              inputStyle={{ width: "100%" }}
              readOnly={step === 1}
              text={context}
              handleText={setContext}
              validationMessage={contextValidationMessage}
            />

            <Stack
              direction="row"
              flexWrap="wrap"
              justifyContent="center"
              gap={2.5}
              sx={{ py: "1.25rem" }}
            >
              {step === 0 ? (
                <OutlinedButton
                  caption={t("confirm")}
                  fontColor="primary.main"
                  sx={{ borderColor: "primary.main" }}
                  onClick={onNext}
                />
              ) : (
                <>
                  <OutlinedButton
                    caption={t("back")}
                    fontColor="primary.main"
                    sx={{ borderColor: "primary.main" }}
                    onClick={onBack}
                  />
                  <Button
                    variant="contained"
                    sx={{
                      p: "10px",
                      width: 240,
                      borderRadius: 0,
                      border: "1px solid",
                      borderColor: "primary.main",
                      ":hover": {
                        bgcolor: "#2DA389",
                        borderColor: "#2DA389",
                      },
                    }}
                    onClick={onNext}
                  >
                    <Typography.DetailText sx={{ color: "background.default" }}>
                      {t("send")}
                    </Typography.DetailText>
                  </Button>
                </>
              )}
            </Stack>
          </Stack>
        </Box>
      </Container>
    </>
  )
}
